// useFocusMainOnNavigate — SPA route-change focus management.
//
// A browser moves focus on a real page load; a client-side route change does
// not — so a keyboard / screen-reader user who activates a nav link is left with
// focus on the old control and no signal the page changed. On each pathname
// change (after the first render) this moves focus to the given element — the
// shell's `<main id="main-content" tabIndex={-1}>` landmark — so they land on the
// new page's content. `preventScroll` so it composes with <ScrollRestoration>
// (which owns scroll position). Shell-owned + inherited by every authed page.
// A route change never pulls the focus out of a dialog that is still OPEN — Back
// from a page to a list whose peek was open lands where a fresh open does, in the
// sheet, which took the focus as it opened (a layout effect, before this one) —
// while a dialog CLOSING with the route change (the phone menu's link: its drawer
// exits as the page changes, React Aria's `data-exiting`) lets it land on main.
// WCAG 2.4.3 Focus Order (+ general SPA SR guidance).

import { useEffect, useRef, type RefObject } from 'react';
import { useLocation } from 'react-router-dom';

export function useFocusMainOnNavigate(ref: RefObject<HTMLElement | null>): void {
  const { pathname } = useLocation();
  const isFirst = useRef(true);
  useEffect(() => {
    // Skip the initial mount — the user just arrived; don't yank focus.
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }
    const dialog = document.activeElement?.closest('[role="dialog"]');
    if (dialog && !dialog.closest('[data-exiting]')) return;
    ref.current?.focus({ preventScroll: true });
  }, [pathname, ref]);
}
